/* eslint-disable @typescript-eslint/no-use-before-define */
import { el } from "@dhx/ts-common/dom";
import { Diagram, ItemConfig, ILineConfig } from "@dhx/ts-diagram";
import { getIcon } from "../configs/icons";
import { Controls, getLength } from "./Controls";

export class Resize {
	private _diagram: Diagram;
	private _controls: Controls;
	private _isOrgType: boolean;

	constructor(controls: Controls, diagram: Diagram) {
		this._controls = controls;
		this._diagram = diagram;
		this._isOrgType = ["org", "mindmap"].includes(this._diagram.config.type);
	}
	getPoints(item: ItemConfig, size: any) {
		if (!item) {
			return;
		}

		const type = item.$item.getBaseType();
		const box = item.$item.getBox();
		const grip = this._isOrgType ? 10 : 8;
		const scale = this._diagram.config.scale;
		let gap = 0;

		if (type === "line") {
			gap = 2;
		}

		const points = getRectPoints(item, scale).map(p =>
			el(".dhx_resize_grip", {
				style: {
					cursor: p.cursor,
					position: "absolute",
					top: p.y - gap - grip / 2,
					left: p.x - gap - grip / 2,
					pointerEvents: "auto",
					outline: "none",
				},
				tabindex: -1,
				class: p.$break
					? "dhx_break_point"
					: p.$slide
					? `dhx_slide_point dhx_slide_point__${p.$slide}`
					: "",
				_data: { resizer: this._controls, p },
			})
		);

		const isGroup = type === "group" || type === "swimlane";
		const width = item[isGroup ? "$width" : "width"] * scale;
		const height = item[isGroup ? "$height" : "height"] * scale;

		const angleResolve = item.angle ? item.angle === 0 || item.angle === 360 : true;
		const isPoints = (angleResolve && (type === "shape" || isGroup)) || type === "line";
		const isGroupChild = item?.$group;

		return el(
			".dhx_resizer",
			{
				style: {
					width,
					height,
					position: "absolute",
					top: (box.top - size.top) * scale,
					left: (box.left - size.left) * scale,
					pointerEvents: "none",
					transform: `rotate(${item.angle || 0}deg)`,
				},
			},
			[
				(type === "shape" || isGroup) &&
					el("div", {
						class: "dhx_diagram_item",
						dhx_id: item.id,
						style: {
							width,
							height,
							top: 0,
							left: 0,
							background: "none",
							outline: "1px dashed #1876d2",
							position: "absolute",
							zIndex: 0,
							pointerEvents: "none",
						},
					}),
				type === "shape" &&
					!this._isOrgType &&
					!isGroupChild &&
					el(
						".dhx_shape_rotate",
						{
							style: {
								width: 20,
								height: 20,
								position: "absolute",
								top: height / 2 - 10,
								left: width + 10,
								zIndex: 10000,
								pointerEvents: "auto",
							},
							_data: { resizer: this._controls, p: { rotate: true } },
						},
						[getIcon("rotate")]
					),
			].concat(isPoints ? points : null)
		);
	}
}

export function getConnectorPoints(item: ILineConfig, scale: number) {
	const start = { ...item.points[0] };
	start.x *= scale;
	start.y *= scale;
	const pointRadius = 2;
	const slidePoints = [];
	const points = item.points.map((point: any, i, arr) => {
		point = { ...point };
		point.x *= scale;
		point.y *= scale;
		point.cursor = "pointer";
		point.i = i;
		if (i === 0) {
			point.side = "from";
		}
		if (i === item.points.length - 1) {
			point.side = "to";
		}
		if (item.connectType !== "flex") {
			if (arr[i + 1]) {
				const nextPoint = { ...arr[i + 1] };
				nextPoint.x *= scale;
				nextPoint.y *= scale;
				const afterNextPoint = arr[i + 2] && {
					x: arr[i + 2].x,
					y: arr[i + 2].y,
				};
				const prevPoint = arr[i - 1] && {
					x: arr[i - 1].x,
					y: arr[i - 1].y,
				};
				/* slider can be inserted when line has zigzag (doubleElbow) form like
							   horizontal slider
									_______
										  |
										  0  <-- slider point here doing parallel slide
										  |
										  ________

										or

								vertical slider

									|
									|
									|===0====
											|
											|
											|

				*/
				const isDoubleElbow =
					prevPoint &&
					nextPoint &&
					afterNextPoint &&
					(prevPoint.x === point.x || prevPoint.y === point.y) &&
					(nextPoint.x === point.x || nextPoint.y === point.y) &&
					nextPoint.x !== prevPoint.x &&
					nextPoint.y !== prevPoint.y &&
					(afterNextPoint.x === nextPoint.x || afterNextPoint.y === nextPoint.y) &&
					afterNextPoint.x !== point.x &&
					afterNextPoint.y !== point.y;

				const dist = getLength(point, nextPoint);
				if (dist > 20) {
					const x = (point.x + nextPoint.x) / 2;
					const y = (point.y + nextPoint.y) / 2;
					if (isDoubleElbow) {
						slidePoints.push({
							x: x - start.x + pointRadius / 2,
							y: y - start.y + pointRadius / 2,
							dx: x / scale,
							dy: y / scale,
							$slide: point.x === nextPoint.x ? "horizontal" : "vertical",
							i: i + 1,
						});
					}
				}
			}
		}
		point.dx = point.x;
		point.dy = point.y;

		point.x = point.x - start.x + pointRadius;
		point.y = point.y - start.y + pointRadius;
		return point;
	});
	return [...points, ...slidePoints];
}

export function getRectPoints(item: ItemConfig, scale: number) {
	const type = item.$item.getBaseType();

	if (type === "line") {
		return getConnectorPoints(item as ILineConfig, scale);
	}

	const isGroup = type === "group" || type === "swimlane";
	const width = item[isGroup ? "$width" : "width"] * scale;
	const height = item[isGroup ? "$height" : "height"] * scale;

	const x = 0;
	const y = 0;

	return [
		{
			cursor: "nw-resize",
			x,
			y,
			dx: 1,
			dy: 1,
		},
		{
			cursor: "n-resize",
			x: x + width / 2,
			y: y,
			dx: 0,
			dy: 1,
		},
		{
			cursor: "ne-resize",
			x: x + width,
			y,
			dx: -1,
			dy: 1,
		},
		{
			cursor: "e-resize",
			x: x + width,
			y: y + height / 2,
			dx: -1,
			dy: 0,
		},
		{
			cursor: "se-resize",
			x: x + width,
			y: y + height,
			dx: -1,
			dy: -1,
		},
		{
			cursor: "s-resize",
			x: x + width / 2,
			y: y + height,
			dx: 0,
			dy: -1,
		},
		{
			cursor: "sw-resize",
			x,
			y: y + height,
			dx: 1,
			dy: -1,
		},
		{
			cursor: "w-resize",
			x: x,
			y: y + height / 2,
			dx: 1,
			dy: 0,
		},
	];
}
